import { baseApi } from '@store/baseApi';
import type { ContainerResponse } from '@store/generated/container';
import type { SemanticTileComponent } from '@store/generated/semantic';

/**
 * Semantic-layer container query. Returns the SDUI container — a fast
 * list of lazy tile descriptors — which the page renders via ComponentSlot;
 * each tile resolves on demand through the shared lazy loader.
 *
 * Org is scoped fail-closed from the auth principal on the backend; entity_id
 * is echoed only for the cache key. Injected on baseApi (no new reducer).
 */

const SEMANTIC_METRICS_TAG = 'SemanticMetrics' as const;

export type SemanticContainerArgs = {
  organizationId?: string;
  windowDays?: number;
};

export type SemanticComponentArgs = {
  slug: string;
};

export const semanticMetricsApi = baseApi
  .enhanceEndpoints({ addTagTypes: [SEMANTIC_METRICS_TAG] })
  .injectEndpoints({
    endpoints: build => ({
      getSemanticContainer: build.query<ContainerResponse, SemanticContainerArgs>({
        query: ({ organizationId, windowDays }) => ({
          url: '/v2/provider/semantic/analytics/container',
          params: { entity_id: organizationId, window_days: windowDays },
        }),
        providesTags: (result, _error, { organizationId }) =>
          result ? [{ type: SEMANTIC_METRICS_TAG, id: organizationId ?? 'me' }] : [],
      }),
      // One computed metric tile — the drill-in detail page. The component
      // carries the full SemanticMetricTilePayload, including the drilldown
      // pointer the embedded reports console runs.
      getSemanticComponent: build.query<SemanticTileComponent, SemanticComponentArgs>({
        query: ({ slug }) => ({
          url: `/v2/provider/semantic/analytics/components/${slug}`,
        }),
        providesTags: (result, _error, { slug }) =>
          result ? [{ type: SEMANTIC_METRICS_TAG, id: `component:${slug}` }] : [],
      }),
    }),
  });

export const { useGetSemanticContainerQuery, useGetSemanticComponentQuery } =
  semanticMetricsApi;
